import { apiClient } from '@/core/apiClient';
import { publicClient } from '@/core/publicClient';
import type {
  AdopterProfile,
  AdopterPreference,
  AdoptionFormTemplate,
  AdoptionFormResponse,
  AdoptionApplication,
  AdoptionApplicationTimeline,
  Adoption,
  SubmitFormRequest,
  CreateApplicationRequest,
  UpdateApplicationStatusRequest,
  CreateAdoptionRequest,
} from './types';

/**
 * API para el sistema de adopción
 */
export const adoptionApi = {
  // ============================================
  // Formularios Dinámicos
  // ============================================

  /**
   * Obtiene la plantilla de formulario activa
   * @param shelterId - ID del refugio (opcional)
   * @param code - Código del formulario (opcional)
   */
  getActiveTemplate: async (
    shelterId?: number,
    code?: string
  ): Promise<AdoptionFormTemplate> => {
    const params = new URLSearchParams();
    if (shelterId) params.append('shelterId', shelterId.toString());
    if (code) params.append('code', code);

    const response = await apiClient.get(
      `/adoption-forms/active?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Lista todas las plantillas de formularios
   * @param shelterId - Filtrar por refugio
   * @param code - Filtrar por código
   * @param isActive - Solo activas
   */
  listTemplates: async (params?: {
    shelterId?: number;
    code?: string;
    isActive?: boolean;
  }): Promise<AdoptionFormTemplate[]> => {
    const searchParams = new URLSearchParams();
    if (params?.shelterId) searchParams.append('shelterId', params.shelterId.toString());
    if (params?.code) searchParams.append('code', params.code);
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());

    const response = await apiClient.get(
      `/adoption-form-templates?${searchParams.toString()}`
    );
    return response.data;
  },

  listAdminTemplates: async (params?: {
    shelterId?: number | null;
    code?: string;
    isActive?: boolean;
  }): Promise<AdoptionFormTemplate[]> => {
    const searchParams = new URLSearchParams();
    if (params?.shelterId !== undefined) {
      if (params.shelterId === null) searchParams.append('global', 'true');
      else searchParams.append('shelterId', params.shelterId.toString());
    }
    if (params?.code) searchParams.append('code', params.code);
    if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());

    const response = await apiClient.get(
      `/admin/adoption-form-templates?${searchParams.toString()}`
    );
    return response.data;
  },

  createAdminTemplateVersion: async (payload: {
    shelterId?: number | null;
    code?: string;
    name: string;
    description?: string;
    schemaJson: AdoptionFormTemplate['schemaJson'];
  }): Promise<AdoptionFormTemplate> => {
    const response = await apiClient.post('/admin/adoption-form-templates', payload);
    return response.data;
  },

  /**
   * Envía respuestas de formulario de adopción
   * @param templateId - ID de la plantilla
   * @param data - Datos del formulario
   */
  submitFormResponse: async (
    templateId: number,
    data: SubmitFormRequest
  ): Promise<AdoptionFormResponse> => {
    const response = await apiClient.post(
      `/adoption-forms/${templateId}/responses`,
      data
    );
    return response.data;
  },

  // ============================================
  // Perfil del Adoptante
  // ============================================

  /**
   * Obtiene el perfil del adoptante actual
   */
  getMyProfile: async (): Promise<AdopterProfile | null> => {
    try {
      const response = await apiClient.get('/adopters/me/profile');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Obtiene el perfil de un adoptante específico
   * @param id - ID del adoptante
   */
  getProfile: async (id: number): Promise<AdopterProfile> => {
    const response = await apiClient.get(`/adopters/${id}/profile`);
    return response.data;
  },

  /**
   * Actualiza el perfil del adoptante
   * @param id - ID del adoptante
   * @param data - Datos a actualizar
   */
  updateProfile: async (
    id: number,
    data: Partial<AdopterProfile>
  ): Promise<void> => {
    await apiClient.put(`/adopters/${id}/profile`, data);
  },

  upsertMyProfile: async (
    data: Partial<AdopterProfile>
  ): Promise<AdopterProfile> => {
    const response = await apiClient.put('/adopters/me/profile', data);
    return response.data;
  },

  // ============================================
  // Preferencias del Adoptante
  // ============================================

  /**
   * Obtiene las preferencias del adoptante actual
   */
  getMyPreferences: async (): Promise<AdopterPreference | null> => {
    try {
      const response = await apiClient.get('/adopters/me/preferences');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Obtiene las preferencias de un adoptante específico
   * @param id - ID del adoptante
   */
  getPreferences: async (id: number): Promise<AdopterPreference> => {
    const response = await apiClient.get(`/adopters/${id}/preferences`);
    return response.data;
  },

  /**
   * Crea o actualiza las preferencias del adoptante
   * @param id - ID del adoptante
   * @param data - Datos de preferencias
   */
  updatePreferences: async (
    id: number,
    data: Partial<AdopterPreference>
  ): Promise<AdopterPreference> => {
    const response = await apiClient.put(`/adopters/${id}/preferences`, data);
    return response.data;
  },

  // ============================================
  // Solicitudes de Adopción
  // ============================================

  /**
   * Crea una nueva solicitud de adopción
   * @param data - Datos de la solicitud
   */
  createApplication: async (
    data: CreateApplicationRequest
  ): Promise<AdoptionApplication> => {
    const response = await apiClient.post('/adoptions/applications', data);
    return response.data;
  },

  /**
   * Obtiene las solicitudes del adoptante actual
   */
  getMyApplications: async (): Promise<AdoptionApplication[]> => {
    const response = await apiClient.get('/adoptions/applications/me');
    return response.data;
  },

  /**
   * Lista solicitudes de adopción
   * @param params - Filtros de búsqueda
   */
  listApplications: async (params?: {
    shelterId?: number;
    animalId?: number;
    status?: string;
    filter?: Record<string, any>;
  }): Promise<AdoptionApplication[]> => {
    const searchParams = new URLSearchParams();
    if (params?.shelterId) searchParams.append('shelterId', params.shelterId.toString());
    if (params?.animalId) searchParams.append('animalId', params.animalId.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.filter) searchParams.append('filter', JSON.stringify(params.filter));

    const response = await apiClient.get(
      `/adoptions/applications?${searchParams.toString()}`
    );
    return response.data;
  },

  /**
   * Verifica si el usuario actual ya tiene una solicitud para un animal específico
   * @param animalId - ID del animal
   */
  checkExistingApplication: async (animalId: number): Promise<boolean> => {
    try {
      const response = await apiClient.get(`/adoptions/applications`, {
        params: { animalId, pageSize: 1 }
      });
      const data = response.data;
      // Si hay al menos una aplicación para este animal, retornar true
      return (data.data && data.data.length > 0) || false;
    } catch (error) {
      // En caso de error, asumir que no hay aplicación
      return false;
    }
  },

  /**
   * Obtiene una solicitud específica por ID
   * @param id - ID de la solicitud
   */
  getApplicationById: async (id: number): Promise<AdoptionApplication> => {
    const response = await apiClient.get(`/adoptions/applications/${id}`);
    return response.data;
  },

  /**
   * Actualiza una solicitud (por ejemplo, notas internas)
   */
  updateApplication: async (id: number, data: Partial<AdoptionApplication> & Record<string, any>) => {
    const response = await apiClient.patch(`/adoptions/applications/${id}`, data);
    return response.data;
  },

  /**
   * Obtiene el timeline de eventos de una solicitud
   * @param id - ID de la solicitud
   */
  getApplicationTimeline: async (id: number): Promise<AdoptionApplicationTimeline[]> => {
    const response = await apiClient.get(`/adoptions/applications/${id}/timeline`);
    return response.data;
  },

  /**
   * Actualiza el estado de una solicitud
   * @param id - ID de la solicitud
   * @param data - Nuevo estado
   */
  updateApplicationStatus: async (
    id: number,
    data: UpdateApplicationStatusRequest
  ): Promise<AdoptionApplication> => {
    const response = await apiClient.patch(
      `/adoptions/applications/${id}/status`,
      data
    );
    return response.data;
  },

  /**
   * Aprueba una solicitud (endpoint dedicado)
   */
  approveApplication: async (id: number): Promise<AdoptionApplication> => {
    const response = await apiClient.post(`/adoption-application/${id}/approve`);
    return response.data;
  },

  // ============================================
  // Adopción Efectiva
  // ============================================

  /**
   * Crea una adopción efectiva (finaliza el proceso)
   * @param data - Datos de la adopción
   */
  createAdoption: async (data: CreateAdoptionRequest): Promise<Adoption> => {
    const response = await apiClient.post('/adoptions', data);
    return response.data;
  },

  /**
   * Sube el contrato firmado para una adopción aprobada
   */
  uploadContract: async (adoptionId: number, file: File): Promise<Adoption> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/adoption/${adoptionId}/upload-contract`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadApplicationContract: async (applicationId: number, file: File): Promise<Adoption> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/adoptions/applications/${applicationId}/contract`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // ============================================
  // Helpers
  // ============================================

  /**
   * Verifica si el usuario actual tiene un perfil de adoptante completo
   */
  hasCompleteProfile: async (): Promise<boolean> => {
    try {
      const profile = await adoptionApi.getMyProfile();
      if (!profile) return false;

      // Verificar campos mínimos requeridos
      return !!(
        profile.city &&
        profile.housingType &&
        profile.housingOwnership
      );
    } catch {
      return false;
    }
  },

  /**
   * Obtiene el perfil completo del adoptante (perfil + preferencias)
   */
  getCompleteProfile: async () => {
    const [profile, preferences] = await Promise.all([
      adoptionApi.getMyProfile(),
      adoptionApi.getMyPreferences(),
    ]);

    return {
      profile,
      preferences,
      isComplete: profile !== null,
    };
  },
};
